import { useState } from 'preact/hooks';
import { type DrawingEntry, type ExpenseEntry } from '@khata/core';
import {
  Empty,
  MonthPicker,
  PageHead,
  Row,
  SearchBox,
  ShowMore,
  VoidedToggle,
  periodLabel,
} from '../components/index.js';
import { dShort, taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { entries, master, state, today } from '../lib/khata.js';
import { accountName } from '../lib/labels.js';
import { listEntries, matches, monthsWithEntries, periodRange } from '../lib/lists.js';
import { usePaged } from '../lib/paging.js';
import { listPeriod, openSheet, showVoided } from '../lib/ui.js';

export function Money() {
  const s = state.value;
  const m = master.value;
  const [q, setQ] = useState('');
  const pick = (e: { type: string }): e is ExpenseEntry | DrawingEntry =>
    e.type === 'expense' || e.type === 'drawing';
  const range = periodRange(listPeriod.value, today.value);
  const inPeriod = listEntries(s, entries.value, pick, showVoided.value, range);
  const rows = q.trim()
    ? inPeriod.filter(({ entry: e }) =>
        matches(
          `${e.type === 'expense' ? e.purpose : e.who} ${e.note} ${accountName(m, e.accountId)}`,
          q,
        ),
      )
    : inPeriod;
  const live = rows.filter((r) => !r.voided);
  const draw = live
    .filter((r) => r.entry.type === 'drawing')
    .reduce((tt, r) => tt + r.entry.amount, 0);
  const exp = live
    .filter((r) => r.entry.type === 'expense')
    .reduce((tt, r) => tt + r.entry.amount, 0);
  const paged = usePaged(rows, listPeriod.value.kind === 'all' ? 300 : 1000);

  return (
    <>
      <PageHead
        title={t('money_title')}
        sub={`${periodLabel()} · ${t('money_drawing')} ${taka(draw as never)} · ${t('money_expense')} ${taka(exp as never)}`}
      >
        <button
          type="button"
          class="btn btn-primary"
          onClick={() => openSheet({ kind: 'expense' })}
        >
          {t('money_expense')}
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          onClick={() => openSheet({ kind: 'drawing' })}
        >
          {t('money_drawing')}
        </button>
      </PageHead>
      <SearchBox value={q} onInput={setQ} placeholder={t('search_money')} />
      <MonthPicker months={monthsWithEntries(entries.value)} />
      <VoidedToggle />
      <div class="list">
        {rows.length === 0 && <Empty>{t('money_empty')}</Empty>}
        {paged.visible.map(({ entry: e, voided }) => (
          <Row
            key={e.id}
            title={e.type === 'expense' ? e.purpose : e.who}
            sub={`${dShort(e.date)} · ${e.type === 'expense' ? t('money_expense') : t('money_drawing')} · ${accountName(m, e.accountId)}${e.note ? ' · ' + e.note : ''}`}
            amount={'−' + taka(e.amount)}
            amountTone={voided ? 'muted' : 'ink'}
            amountSub={voided ? t('voided') : undefined}
            strike={voided}
            onClick={() => openSheet({ kind: 'entry', id: e.id })}
          />
        ))}
      </div>
      <ShowMore shown={paged.shown} total={paged.total} onMore={paged.more} />
    </>
  );
}
